/**
 * Das Vergrößern in der Vorschau (Schritt 7, ARCHITEKTUR.md 6.2).
 *
 * Die Geometrie des Rahmens steht in `shared/rahmen.ts`, weil der Export sie später
 * ebenso braucht. Was hier steht, braucht **nur** der Browser: wie weit vergrößert werden
 * darf und wohin gescrollt wird, wenn sich der Zoom ändert.
 *
 * **Warum ein Regler und keine Zwei-Finger-Geste** (Carolin, 24.09.2026): Der Pinch-Code
 * aus `wizard-flow` skaliert ein **Element**, keine Fläche — ein Flächen-Zoom wäre neu
 * geschrieben, samt Grenzen und abgebrochenen Zeigern, und wäre nicht tastaturtauglich
 * (2.4, 3.8). Geschoben wird über das native Scrollen: dieselbe Geste wie im Editor, in
 * Schritt 4 erkämpft und bei 60 Figuren mit 120 Bildern/s gemessen.
 *
 * ⚠️ **Der Nachteil bleibt stehen:** Wer auf einem Telefon zuerst zum Pinch greift,
 * bekommt keine Rückmeldung. Beobachtungspunkt für Schritt 8.
 *
 * Alles hier ist rein — Zahlen rein, Zahlen raus, kein DOM.
 */

/**
 * Wie weit der Regler höchstens geht, als harte Obergrenze über der gerechneten.
 *
 * Die gerechnete Grenze ist die **Originalgröße des Vorschaubildes**: mehr Zoom zeigt
 * keine zusätzliche Schärfe, weil die kleine Fassung 400 px lange Kante hat
 * (`src/lib/vorschau.ts`). Bei 60 Figuren liegt sie bei grob 9,5.
 *
 * Der Deckel fängt den Fall ab, in dem eine einzelne winzige Reihe einen dreistelligen
 * Faktor ergäbe — dann stünde im Rahmen eine Fläche von zigtausend Pixeln, und das ist
 * der Speicherfehler, den kein Test findet, weil er nur auf einem Telefon auftritt.
 */
export const ZOOM_DECKEL = 10;

/** Schrittweite des Reglers. Fein genug, dass sich der Zoom stufenlos anfühlt, grob
 *  genug, dass die Tastaturbedienung nicht hundert Schritte braucht. */
export const ZOOM_SCHRITT = 0.1;

/**
 * Von „ganzes Blatt" bis „Originalgröße".
 *
 * `min` ist immer 1 — das ist der eingepasste Rahmen, bei dem alles zu sehen ist. `max`
 * ist der Kehrwert des Einpassfaktors: genau dort steht die Reihe in der Größe, in der
 * sie auch im Editor liegt.
 *
 * Ist die Reihe schon vergrößert eingepasst (wenige Figuren, `faktor >= 1`), gibt es
 * nichts zu zoomen und `max` ist 1. Ein Regler ohne Wirkung wäre schlimmer als keiner.
 */
export function zoomGrenzen(faktor: number): { min: number; max: number } {
  if (!Number.isFinite(faktor) || faktor <= 0) return { min: 1, max: 1 };
  return { min: 1, max: Math.min(ZOOM_DECKEL, Math.max(1, 1 / faktor)) };
}

/**
 * Wohin gescrollt wird, wenn sich der Zoom ändert: **der Mittelpunkt bleibt stehen**.
 *
 * Ohne das springt jedes Vergrößern an den Anfang der Reihe — man sucht die Figur, die
 * man gerade ansah, in 3.000 px wieder. Gilt waagerecht **und** senkrecht: bei hohem Zoom
 * füllt das weiße Papier über den Figuren den halben Bildschirm, und wer dort landet,
 * findet ohne Zufall nicht zurück.
 *
 * `altSichtbar` und `neuSichtbar` sind getrennte Werte, weil das Fenster mitwächst: die
 * Vorschau ist beim Einpassen 122 px hoch und darf sich beim Zoomen bis zur
 * Bildschirmhöhe strecken, bevor senkrecht gescrollt wird.
 */
export function scrollNachZoom(
  altScroll: number,
  altSichtbar: number,
  neuSichtbar: number,
  altZoom: number,
  neuZoom: number,
  maxScroll: number,
): number {
  if (!Number.isFinite(altZoom) || altZoom <= 0) return 0;
  if (!Number.isFinite(neuZoom) || !Number.isFinite(altScroll)) return 0;
  if (!Number.isFinite(altSichtbar) || !Number.isFinite(neuSichtbar)) return 0;

  const mitte = (altScroll + altSichtbar / 2) * (neuZoom / altZoom);
  const neu = mitte - neuSichtbar / 2;
  // Die Klammer ist nicht Kosmetik: ein negativer `scrollLeft` wird vom Browser still
  // auf 0 gezogen, ein zu großer still auf das Ende — beides verschiebt den Mittelpunkt,
  // ohne dass es auffällt. Und `maxScroll` ist beim Herauszoomen auf 1 gleich 0, dann
  // muss hier 0 herauskommen und nichts anderes.
  const grenze = Number.isFinite(maxScroll) ? Math.max(0, maxScroll) : 0;
  return Math.min(grenze, Math.max(0, neu));
}
